import { Link, createFileRoute } from "@tanstack/react-router";
import { useSuspenseQuery } from "@tanstack/react-query";

import { HeroFeature } from "@/components/site/HeroFeature";
import { MeetingCard } from "@/components/site/MeetingCard";
import { NewDiplomatsSignup } from "@/components/site/NewDiplomatsSignup";
import { PhotoCard } from "@/components/site/PhotoCard";
import { LoadMoreMeetingsLink } from "@/components/site/LoadMoreButton";
import { meetingsQuery, type Meeting } from "@/lib/content";
import { FC_DATA_ORIGIN } from "@/integrations/fc/config";
import featuredFallback from "@/assets/featured-1280.webp.asset.json";

export const Route = createFileRoute("/")({
  staticData: { sitemap: true },
  // Await data so the first HTML already contains the hero <img>; the browser
  // starts downloading photos immediately instead of after hydration.
  loader: async ({ context }) => {
    const meetings = await context.queryClient.ensureQueryData(meetingsQuery(10));
    const featured = meetings.find((meeting) => meeting.is_upcoming) ?? meetings[0];
    return { hero: featured ? { src: featured.cover_image_url ?? featuredFallback.url, srcSet: featured.cover_image_srcset } : undefined };
  },
  head: ({ loaderData }) => ({
    meta: [
      { title: "Foreign Council On Economic Relations — Global Economic Diplomacy" },
      {
        name: "description",
        content:
          "Translating strategic government priorities into investment opportunities in infrastructure, critical raw materials, and strategic industries worldwide.",
      },
      { property: "og:title", content: "Foreign Council On Economic Relations — Global Economic Diplomacy" },
      {
        property: "og:description",
        content:
          "Translating strategic government priorities into investment opportunities in infrastructure, critical raw materials, and strategic industries worldwide.",
      },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://foreign-council.org/" },
      { name: "twitter:card", content: "summary_large_image" },
      ...(loaderData?.hero?.src.startsWith("https://") ? [
        { property: "og:image", content: loaderData.hero.src },
        { name: "twitter:image", content: loaderData.hero.src },
      ] : []),
    ],
    links: [
      { rel: "canonical", href: "https://foreign-council.org/" },
      { rel: "preconnect", href: FC_DATA_ORIGIN },
      // React emits the matching responsive hero preload into the first head.
      // A manual route link creates a redundant tag, so keep the automatic one.
    ],
  }),
  component: HomePage,
});

function HomePage() {
  const { data } = useSuspenseQuery(meetingsQuery(10));
  return <HomeContent meetings={data} />;
}

function HomeContent({ meetings }: { meetings: Meeting[] }) {


  const featured = meetings.find((meeting) => meeting.is_upcoming) ?? meetings[0];
  const cards = meetings.slice(0, 6);
  const latest = meetings.filter((meeting) => !meeting.is_upcoming);
  const latestFeatured = latest[0];
  const latestGrid = latest.slice(1, 7);

  return (
    <>
      {featured && <HeroFeature meeting={featured} priority headingLevel={1} />}

      <section className="bg-surface">
        <div className="mx-auto w-full max-w-[1400px] px-5 py-16 lg:px-8 lg:py-20">
          <h2 className="fc-h2 fc-rule text-foreground">Meetings</h2>

          <div className="mt-12 grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
            {cards.map((meeting) => (
              <MeetingCard key={meeting.id} meeting={meeting} />
            ))}
          </div>

          <div className="mt-10 flex justify-end">
            <Link
              to="/meetings"
              className="bg-[oklch(0.35_0_0)] px-6 py-3 fc-body text-primary-foreground transition-opacity hover:opacity-85"
            >
              All Meetings
            </Link>
          </div>
        </div>
      </section>

      <NewDiplomatsSignup />

      <section className="bg-surface">
        <div className="mx-auto w-full max-w-[1400px] px-5 py-16 lg:px-8 lg:py-20">
          <h2 className="fc-h2 fc-rule text-foreground">Latest</h2>

          {latestFeatured && (
            <div className="mt-12">
              <HeroFeature meeting={latestFeatured} showBadge={false} showReadMore={false} headingLevel={3} contained />
            </div>
          )}

          <div className="mt-10 grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
            {latestGrid.map((meeting) => (
              <PhotoCard key={meeting.id} meeting={meeting} />
            ))}
          </div>

          <div className="mt-12 flex justify-center">
            <LoadMoreMeetingsLink className="inline-block bg-[oklch(0.35_0_0)] px-6 py-3 fc-body text-primary-foreground transition-opacity hover:opacity-85" />
          </div>
        </div>
      </section>
    </>
  );
}
